import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Building2,
  Package,
  TrendingUp,
  Calendar,
  ChevronRight,
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  Target,
  Wrench,
  Gauge,
  Truck,
  X,
  RotateCcw,
  Layers,
} from "lucide-react";

// ---- Semi-circular progress gauge (presentation only — consumes existing stats) ----
const GAUGE_STATUS = (actual, expected) => {
  if (!expected || expected <= 0) return { color: "#0ca30c", word: "On plan" };
  const r = actual / expected;
  if (r >= 1) return { color: "#0ca30c", word: "Ahead of schedule" };
  if (r >= 0.8) return { color: "#fab219", word: "Slightly behind" };
  if (r >= 0.5) return { color: "#ec835a", word: "Behind schedule" };
  return { color: "#d03b3b", word: "Critically behind" };
};

/* Reorderable widget grid — presentation only. Children render unchanged; each
   is wrapped in a draggable cell. Order is a per-unit localStorage preference,
   keyed by data-testid, so conditional cards and new tiles slot in safely.

   ROUND CH — hold-and-move with the hand cursor. The old HTML5 `draggable`
   version needed a native drag gesture, gave no hand feedback on the cards that
   carry their own pointer cursor, and did nothing at all on touch. This is a
   pointer-event drag instead: every bubble shows the open-hand cursor, press and
   move ~6px (or long-press on touch) to pick it up, the bubble then follows your
   finger/cursor while the others slide out of the way to show where it will land,
   and releasing saves the new order. A press that never moves is still a normal
   click, so the clickable cards (Shortage / Maintenance Prediction) keep working.
   Esc cancels and puts the bubble back. */
function DraggableGrid({ className, storageKey, children, enabled = true }) {
  const entries = React.Children.toArray(children).filter(Boolean).map((el, i) => ({
    id: el?.props?.["data-testid"] || el?.props?.testId || `w-${i}`, el,
  }));
  const [order, setOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || "[]"); } catch { return []; }
  });
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  // Mutable gesture state — refs, not state, so the move handler never goes stale.
  const g = useRef({ id: null, x0: 0, y0: 0, armed: false, dragging: false,
                     moved: false, holdTimer: null, orderAtStart: null });

  const rank = (id) => {
    const i = order.indexOf(id);
    if (i !== -1) return i;
    // A card not yet in the saved order (e.g. a newly-added gauge like Assembly):
    // slot it at its NATURAL JSX position — just after its nearest preceding saved
    // sibling — instead of dumping it at the very end where it can wrap out of view.
    const idx = entries.findIndex((e) => e.id === id);
    for (let j = idx - 1; j >= 0; j--) {
      const p = order.indexOf(entries[j].id);
      if (p !== -1) return p + 0.5;
    }
    return -0.5; // no saved predecessor -> render at the front
  };
  const sorted = [...entries].sort((a, b) => rank(a.id) - rank(b.id));
  const sortedIds = sorted.map((x) => x.id);

  const previewMove = (targetId) => {
    // Read the order from the live ref, never from this closure: these handlers
    // were created at pointerdown and their `sortedIds` froze there, so the drop
    // would save the pre-drag order and the preview would aim at stale slots.
    const ids = [...(live.current.sortedIds || sortedIds)];
    const from = ids.indexOf(g.current.id), to = ids.indexOf(targetId);
    if (from === -1 || to === -1 || from === to) return;
    ids.splice(from, 1);
    ids.splice(to, 0, g.current.id);
    setOrder(ids);           // neighbours shift immediately — live landing preview
  };

  const stopDrag = (save) => {
    if (g.current.holdTimer) clearTimeout(g.current.holdTimer);
    if (g.current.dragging) {
      if (save) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(live.current.sortedIds || sortedIds));
          window.dispatchEvent(new CustomEvent("dash-layout-saved"));
        } catch {}
      } else if (g.current.orderAtStart) {
        setOrder(g.current.orderAtStart);   // Esc — put it back
      }
    }
    g.current = { ...g.current, id: null, armed: false, dragging: false, holdTimer: null };
    setDragId(null);
    setOverId(null);
    setOffset({ x: 0, y: 0 });
    document.body.classList.remove("dash-dragging");
  };

  const begin = () => {
    if (g.current.dragging || !g.current.id) return;
    g.current.dragging = true;
    g.current.orderAtStart = live.current.sortedIds || sortedIds;
    setDragId(g.current.id);
    document.body.classList.add("dash-dragging");
  };

  // Listeners live on WINDOW, not on the cell. setPointerCapture is useless here:
  // reordering makes React move the dragged node in the DOM, and moving a node
  // silently releases its capture — the pointerup then never arrives and the drop
  // is lost. Window listeners survive every reorder.
  const onPointerDown = (e, id) => {
    if (e.button != null && e.button !== 0) return;      // left button / touch only
    if (e.target.closest("button, a, input, select, textarea")) return;
    g.current = { ...g.current, id, x0: e.clientX, y0: e.clientY, armed: true,
                  dragging: false, moved: false, holdTimer: null, orderAtStart: null };
    window.addEventListener("pointermove", onPointerMove, { passive: false });
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerCancel);
    if (e.pointerType === "touch") {
      // Touch: long-press to pick up, so a plain swipe still scrolls the page.
      g.current.holdTimer = setTimeout(() => { if (g.current.armed && !g.current.moved) begin(); }, 260);
    }
  };

  const detach = () => {
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    window.removeEventListener("pointercancel", onPointerCancel);
  };

  const onPointerMove = (e) => {
    if (!g.current.armed) return;
    const dx = e.clientX - g.current.x0, dy = e.clientY - g.current.y0;
    if (!g.current.dragging) {
      if (Math.hypot(dx, dy) < 6) return;                // still a click, not a drag
      g.current.moved = true;
      if (e.pointerType === "touch") { detach(); stopDrag(false); return; }  // let it scroll
      begin();
    }
    e.preventDefault();
    setOffset({ x: dx, y: dy });
    // Hit-test THROUGH the lifted card (it sits under the cursor the whole time).
    const stack = document.elementsFromPoint ? document.elementsFromPoint(e.clientX, e.clientY) : [];
    let tid = null;
    for (const node of stack) {
      const cell = node.closest && node.closest("[data-widget-id]");
      const cid = cell && cell.getAttribute("data-widget-id");
      if (cid && cid !== g.current.id) { tid = cid; break; }
    }
    if (tid) { setOverId(tid); previewMove(tid); }
  };

  const onPointerUp = () => {
    detach();
    const wasDragging = g.current.dragging;
    stopDrag(true);
    if (wasDragging) {           // swallow the click that follows a real drag
      g.current.justDragged = true;
      setTimeout(() => { g.current.justDragged = false; }, 0);
    }
  };

  const onPointerCancel = () => { detach(); stopDrag(false); };

  // Both effects are MOUNT-ONLY. With no dependency array React re-runs the
  // cleanup after every render — and every pointermove renders — so an unmount-
  // shaped cleanup here would tear the drag down a few milliseconds after it
  // started (listeners detached, body class stripped). The refs keep the
  // handlers current without re-subscribing.
  const live = useRef({});
  live.current = { stopDrag, detach, sortedIds };
  useEffect(() => {
    const onKey = (ev) => { if (ev.key === "Escape" && g.current.dragging) live.current.stopDrag(false); };
    const onReset = () => setOrder([]);
    window.addEventListener("keydown", onKey);
    window.addEventListener("dash-layout-reset", onReset);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("dash-layout-reset", onReset);
      live.current.detach();
      document.body.classList.remove("dash-dragging");
    };
  }, []);

  return (
    <div className={className}>
      {sorted.map(({ id, el }, i) => (
        // Drag-to-rearrange is ADMIN-ONLY. For everyone else the card renders as a
        // plain cell — no grab cursor, no "Hold and move to rearrange" handle, no
        // pointer handlers — so the layout cannot be moved.
        enabled ? (
          <div
            key={id}
            data-widget-id={id}
            className={`dash-widget dash-grabbable${dragId === id ? " drag-lift" : ""}${
              overId === id && dragId && dragId !== id ? " drag-over" : ""}`}
            style={dragId === id
              ? { "--dash-i": i, transform: `translate(${offset.x}px, ${offset.y}px) scale(1.03)` }
              : { "--dash-i": i }}
            title="Hold and move to rearrange"
            onPointerDown={(e) => onPointerDown(e, id)}
            onClickCapture={(e) => { if (g.current.justDragged) { e.preventDefault(); e.stopPropagation(); } }}
          >
            {el}
          </div>
        ) : (
          <div key={id} data-widget-id={id} className="dash-widget" style={{ "--dash-i": i }}>
            {el}
          </div>
        )
      ))}
    </div>
  );
}

/* ROUND CH — escape hatch for the drag-to-rearrange: one click puts every bubble
   back in its original order. Only shown once a custom layout actually exists, so
   it never adds noise for someone who has not moved anything. */
function ResetLayoutButton({ keys }) {
  const has = () => keys.some((k) => {
    try { return (JSON.parse(localStorage.getItem(k) || "[]") || []).length > 0; } catch { return false; }
  });
  const [show, setShow] = useState(has);
  useEffect(() => {
    const sync = () => setShow(has());
    window.addEventListener("dash-layout-saved", sync);
    return () => window.removeEventListener("dash-layout-saved", sync);
  });
  if (!show) return null;
  return (
    <button
      type="button"
      data-testid="dash-reset-layout"
      title="Put every card back in its original position"
      onClick={() => {
        keys.forEach((k) => { try { localStorage.removeItem(k); } catch {} });
        window.dispatchEvent(new CustomEvent("dash-layout-reset"));
        setShow(false);
      }}
      className="inline-flex items-center gap-1.5 rounded-full border border-border/50 bg-white/80 dark:bg-slate-800/80 px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground shadow-sm backdrop-blur"
    >
      <RotateCcw className="w-3.5 h-3.5" />
      Reset layout
    </button>
  );
}

/* Status pill for percentage cards — words derived from the SAME thresholds
   the value's color already uses. Presentation only. */
function LevelPill({ pct, hi = 95, lo = 80, words = ["On track", "Attention", "Behind"], light }) {
  const v = Number(pct) || 0;
  const idx = v >= hi ? 0 : v >= lo ? 1 : 2;
  const cls = light
    ? "dash-pill-light"   // frosted white — for the solid accent cards
    : [
        "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
        "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
        "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
      ][idx];
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium align-middle ml-2 ${cls}`}>
      {words[idx]}
    </span>
  );
}

/* Decorative mini trend stroke, as in the reference cards. Pure ornament —
   fixed path, no data behind it (we keep real numbers to real charts). */
const Spark = ({ color = "#10b981", className = "" }) => (
  <svg viewBox="0 0 84 24" className={`h-5 w-24 ${className}`} aria-hidden="true">
    <path d="M2 18 C 12 16, 16 8, 26 12 S 44 20, 54 10 S 72 4, 82 7"
          fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.85" />
  </svg>
);

/* Large compliance ring (reference: pct centred INSIDE the ring, fraction
   beneath it). Same pct/figures the card already shows — presentation only. */
function BigRing({ pct, num, den }) {
  const r = 40, c = 2 * Math.PI * r;
  const clamped = Math.min(Math.max(Number(pct) || 0, 0), 100);
  const [off, setOff] = useState(c);
  useEffect(() => {
    const t = setTimeout(() => setOff(c * (1 - clamped / 100)), 450);
    return () => clearTimeout(t);
  }, [clamped, c]);
  return (
    <div className="relative w-24 h-24 mx-auto dash-ring">
      <svg width="96" height="96" viewBox="0 0 96 96" aria-hidden="true">
        <circle cx="48" cy="48" r={r} fill="none" stroke="currentColor" opacity="0.28" strokeWidth="7" />
        <circle cx="48" cy="48" r={r} fill="none" stroke="#ffffff" strokeWidth="7"
                strokeLinecap="round" className="ring-val"
                strokeDasharray={c} strokeDashoffset={off}
                transform="rotate(-90 48 48)" />
        <circle cx="48" cy="48" r={r} fill="none" stroke="#ffffff" strokeWidth="7"
                strokeLinecap="round" opacity="0.6" className="ring-orbit"
                strokeDasharray={`18 ${c - 18}`} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-tight text-center">
        <span className="text-xl font-bold">{pct}%</span>
        <span className="text-[8.5px] opacity-90">{num},</span>
        <span className="text-[8.5px] opacity-90">{den}</span>
      </div>
    </div>
  );
}

/* Semi-circular needle gauge for Yesterday's Production (reference item 19).
   Driven by the SAME efficiency % the card already reports. */
function MiniGauge({ pct }) {
  const clamped = Math.min(Math.max(Number(pct) || 0, 0), 100);
  const r = 28, cx = 40, cy = 38;
  const semi = Math.PI * r;
  const [off, setOff] = useState(semi);
  useEffect(() => {
    const t = setTimeout(() => setOff(semi * (1 - clamped / 100)), 450);
    return () => clearTimeout(t);
  }, [clamped, semi]);
  // Needle drawn at 0% (pointing left) inside a group that ROTATES to the
  // value — same 1.1s sweep as the arc, so it moves like a fuel-meter needle.
  const [deg, setDeg] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setDeg((clamped / 100) * 180), 450);
    return () => clearTimeout(t);
  }, [clamped]);
  const arc = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;
  return (
    <svg width="80" height="44" viewBox="0 0 80 44" className="dash-ring" aria-hidden="true">
      <path d={arc} fill="none" stroke="currentColor" opacity="0.15" strokeWidth="7" strokeLinecap="round" />
      <path d={arc} fill="none" stroke="#f59e0b" strokeWidth="7" strokeLinecap="round"
            className="ring-val" strokeDasharray={semi} strokeDashoffset={off} />
      <g style={{ transform: `rotate(${deg}deg)`, transformOrigin: `${cx}px ${cy}px`,
                  transition: "transform 6s cubic-bezier(0.22, 1, 0.36, 1)" }}>
        <line x1={cx} y1={cy} x2={cx - (r - 9)} y2={cy} stroke="#0f172a" strokeWidth="2" strokeLinecap="round" />
      </g>
      <circle cx={cx} cy={cy} r="2.5" fill="#0f172a" />
    </svg>
  );
}

/* Small decorative progress ring — sweeps to the SAME pct the card already
   shows; purely visual, no computation of its own. */
function MiniRing({ pct, color }) {
  const r = 20, c = 2 * Math.PI * r;
  const clamped = Math.min(Math.max(Number(pct) || 0, 0), 100);
  const [off, setOff] = useState(c);
  useEffect(() => {
    const t = setTimeout(() => setOff(c * (1 - clamped / 100)), 450);
    return () => clearTimeout(t);
  }, [clamped, c]);
  return (
    <svg width="52" height="52" viewBox="0 0 52 52" className="dash-ring ml-auto shrink-0" aria-hidden="true">
      <circle cx="26" cy="26" r={r} fill="none" stroke="currentColor" opacity="0.12" strokeWidth="5" />
      <circle cx="26" cy="26" r={r} fill="none" stroke={color} strokeWidth="5"
              strokeLinecap="round" className="ring-val"
              strokeDasharray={c} strokeDashoffset={off}
              transform="rotate(-90 26 26)" />
      <circle cx="26" cy="26" r={r} fill="none" stroke={color} strokeWidth="5"
              strokeLinecap="round" opacity="0.5" className="ring-orbit"
              strokeDasharray={`12 ${c - 12}`} />
    </svg>
  );
}

// Identity gradients for the three analytics gauges (reference image):
// press = coral/orange, machine shop = violet, final inspection = cyan/blue.
const GAUGE_HUES = {
  orange: ["#f87171", "#fb923c"],
  violet: ["#7c5cd6", "#a78bfa"],
  cyan:   ["#0891b2", "#38bdf8"],
};

function GaugeArc({ pct, expPct, color, hue = "orange" }) {
  const W = 260, H = 148, cx = W / 2, cy = 136, r = 104, sw = 16;
  const semi = Math.PI * r;
  const [offset, setOffset] = useState(semi);
  useEffect(() => {
    const t = setTimeout(() => setOffset(semi * (1 - Math.min(Math.max(pct, 0), 1))), 450);
    return () => clearTimeout(t);
  }, [pct, semi]);
  const aExp = Math.PI * (1 - Math.min(Math.max(expPct, 0), 1));
  const ex1 = cx + r * Math.cos(aExp), ey1 = cy - r * Math.sin(aExp);
  const ex2 = cx + (r + 13) * Math.cos(aExp), ey2 = cy - (r + 13) * Math.sin(aExp);
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((p) => {
    const a = Math.PI * (1 - p);
    return (
      <line key={p}
        x1={cx + (r - sw / 2 - 5) * Math.cos(a)} y1={cy - (r - sw / 2 - 5) * Math.sin(a)}
        x2={cx + (r - sw / 2 - 11) * Math.cos(a)} y2={cy - (r - sw / 2 - 11) * Math.sin(a)}
        stroke="currentColor" strokeWidth="1.5" opacity="0.35" />
    );
  });
  const [g1, g2] = GAUGE_HUES[hue] || GAUGE_HUES.orange;
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="max-w-full text-muted-foreground">
      <defs>
        <linearGradient id={`ggrad-${hue}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={g1} />
          <stop offset="100%" stopColor={g2} />
        </linearGradient>
      </defs>
      {/* decorative trend curve behind the gauge, as in the reference */}
      <path d="M0 128 C 30 110, 55 124, 90 108 S 150 122, 190 100 S 240 96, 260 86"
        fill="none" stroke={g2} strokeWidth="2" strokeLinecap="round" opacity="0.28" />
      <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none"
        stroke="currentColor" opacity="0.18" strokeWidth={sw} strokeLinecap="round" />
      <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none"
        stroke={`url(#ggrad-${hue})`} strokeWidth={sw} strokeLinecap="round"
        strokeDasharray={semi} strokeDashoffset={offset}
        style={{ transition: "stroke-dashoffset 6s cubic-bezier(.25,.8,.3,1)" }} />
      {/* comet sweeping the full dial, same motion language as the top rings */}
      <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none"
        stroke={`url(#ggrad-${hue})`} strokeWidth={sw} strokeLinecap="round"
        className="gauge-comet" opacity="0.45"
        strokeDasharray={`18 ${semi * 2}`}
        style={{ "--semi": `${semi}px` }} />
      {ticks}
      <line x1={ex1} y1={ey1} x2={ex2} y2={ey2} stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" opacity="0.85" />
      <text x={ex2} y={ey2 - 5} textAnchor="middle" fontSize="9.5" fill="currentColor" opacity="0.8">today</text>
      <text x={cx - r} y={cy + 11} textAnchor="middle" fontSize="9.5" fill="currentColor" opacity="0.6">0%</text>
      <text x={cx + r} y={cy + 11} textAnchor="middle" fontSize="9.5" fill="currentColor" opacity="0.6">100%</text>
    </svg>
  );
}

function GaugeTile({ value, label, color, title }) {
  return (
    <div title={title}
      className="rounded-lg border bg-muted/40 px-3 py-2 transition-transform duration-150 hover:-translate-y-0.5 hover:border-primary/60">
      <p className="text-base font-bold" style={color ? { color } : undefined}>{value}</p>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}

function ProgressGauge({ title, subtitle, plan, cumulative, pending, backlog, testId, hue = "orange", tag }) {
  const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
  const p = Number(plan || 0), c = Number(cumulative || 0), b = Number(backlog || 0);
  const expected = c + b;                       // backlog may be negative (ahead) on the press card
  const pct = p > 0 ? c / p : 0;
  const expPct = p > 0 ? Math.min(Math.max(expected / p, 0), 1) : 0;
  // Once the full month net plan is met (cumulative ≥ plan, pending 0) the card can
  // never read as "behind". The machine-shop backlog is a Σ max(0, expected − made)
  // over ops — it over-counts lagging parts while ignoring over-produced ones — so at
  // 100%+ completion it would otherwise falsely show "slightly behind". Force ahead.
  const planMet = p > 0 && c >= p;
  const st = planMet ? { color: "#0ca30c", word: "Ahead of schedule" } : GAUGE_STATUS(c, expected);
  const behindState = planMet ? "Ahead of schedule" : b > 0 ? "Behind schedule" : b < 0 ? "Ahead of schedule" : "On plan";
  const behindValue = planMet ? 0 : Math.abs(b);
  return (
    <Card className={`dash-gauge-card dash-gtint-${hue}`} data-testid={testId}>
      <CardHeader className="pb-1">
        <CardTitle className="text-base">{title}</CardTitle>
        {subtitle ? <p className="text-xs text-muted-foreground">{subtitle}</p> : null}
        {tag && (
          <span className="inline-block self-start mt-1 rounded-md bg-white/80 dark:bg-slate-800/80 border border-border/50 px-2 py-0.5 text-[10px] font-medium shadow-sm">
            {tag}
          </span>
        )}
      </CardHeader>
      <CardContent>
        <div className="relative flex justify-center">
          <GaugeArc pct={pct} expPct={expPct} color={st.color} hue={hue} />
          <div className="absolute inset-0 flex flex-col items-center justify-end pb-1 pointer-events-none">
            <p className="text-4xl font-extrabold leading-none" style={{ color: st.color }}>
              {(pct * 100).toFixed(1)}<span className="text-lg font-semibold text-muted-foreground">%</span>
            </p>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">complete</p>
          </div>
        </div>
        <div className="flex items-center justify-center gap-2 text-xs my-2.5">
          <span className="w-2 h-2 rounded-full" style={{ background: st.color }} />
          <span className="font-semibold" style={{ color: st.color }}>{st.word}</span>
          <span className="text-muted-foreground">· expected {(expPct * 100).toFixed(0)}% by today</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <GaugeTile value={fmt(p)} label="Total plan" title="Net plan for the month (schedule + extra − opening)" />
          <GaugeTile value={fmt(c)} label="Cumulative" color="#16a34a" title="Produced so far this month" />
          <GaugeTile value={fmt(pending)} label="Pending (month)" color="#d97706" title="Remaining against the full month" />
          <GaugeTile value={fmt(behindValue)} label={behindState} color={st.color}
            title="Expected by today minus produced — state named in words instead of a sign" />
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  // PM Due summary (Maintenance card) — derived from the SAME predicted-maintenance
  // engine the Analytics page uses. Never blocks the dashboard: on any error /
  // no permission it simply stays null and the PM line is hidden.
  const [pmDue, setPmDue] = useState(null); // { upcoming, due, total } | null
  const [rmCoverage, setRmCoverage] = useState(null); // raw-material stock-statement summary | null
  const { selectedUnit, user } = useAuth();
  // Drag-to-rearrange the dashboard cards is reserved for admins (same "admin"
  // definition used across the app: the admin or management role).
  const canArrange = user?.role === "admin" || user?.role === "management";
  const navigate = useNavigate();
  const reqIdRef = React.useRef(0); // F22: stale-response guard
  // Calendar lookup on the Production card — pick any past date to see its production.
  const [prodDate, setProdDate] = useState("");
  const [dateProd, setDateProd] = useState(null);
  const [dateProdLoading, setDateProdLoading] = useState(false);
  const prodDateRef = React.useRef(null);
  const todayISO = new Date().toLocaleDateString("en-CA"); // local YYYY-MM-DD
  const fetchProductionForDate = async (date) => {
    if (!date || !selectedUnit) { setDateProd(null); return; }
    setDateProdLoading(true);
    try {
      const res = await api.get(`/dashboard/production-on-date?unit_id=${selectedUnit.id}&date=${date}`);
      setDateProd(res.data);
    } catch {
      setDateProd(null);
    } finally {
      setDateProdLoading(false);
    }
  };
  // Calendar lookup on the FG Dispatch card.
  const [fgDate, setFgDate] = useState("");
  const [fgDisp, setFgDisp] = useState(null);
  const [fgDispLoading, setFgDispLoading] = useState(false);
  // 🔮 Shortage Prediction bubble — one cheap summary call (server-cached 5 min),
  // full panel lives on the Inventory page. Read-only.
  const [shortPred, setShortPred] = useState(null);
  // 🛠️ Maintenance Prediction bubble + machine-health dialog. Read-only.
  const [maintPred, setMaintPred] = useState(null);
  const [maintOpen, setMaintOpen] = useState(false);
  const [maintRows, setMaintRows] = useState(null);
  const [maintWhy, setMaintWhy] = useState(null);
  useEffect(() => {
    if (!selectedUnit?.id) return;
    let dead = false;
    api.get(`/analytics/shortage-predictions?unit_id=${selectedUnit.id}&summary=true`, { silent403: true })
      .then((r) => { if (!dead) setShortPred(r.data); })
      .catch(() => { if (!dead) setShortPred(null); });
    api.get(`/analytics/maintenance-predictions?unit_id=${selectedUnit.id}&summary=true`, { silent403: true })
      .then((r) => { if (!dead) setMaintPred(r.data); })
      .catch(() => { if (!dead) setMaintPred(null); });
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUnit?.id]);
  const openMachineHealth = async () => {
    setMaintOpen(true);
    if (!maintRows) {
      try {
        const { data } = await api.get(`/analytics/maintenance-predictions?unit_id=${selectedUnit.id}`);
        setMaintRows(data);
      } catch { setMaintRows({ rows: [], counts: {} }); }
    }
  };
  const fgDateRef = React.useRef(null);
  const fetchFgForDate = async (date) => {
    if (!date || !selectedUnit) { setFgDisp(null); return; }
    setFgDispLoading(true);
    try {
      const res = await api.get(`/dashboard/fg-dispatch-on-date?unit_id=${selectedUnit.id}&date=${date}`);
      setFgDisp(res.data);
    } catch {
      setFgDisp(null);
    } finally {
      setFgDispLoading(false);
    }
  };

  useEffect(() => {
    if (selectedUnit) {
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUnit]);

  const fetchDashboardData = async () => {
    // F22: capture the request-id in closure and drop the response if a newer
    // request has fired since (e.g. user rapidly switched units). Prevents
    // Unit-A's stats overwriting Unit-B's after a fast toggle.
    const myReqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const unitParam = `?unit_id=${selectedUnit.id}`;
      const statsRes = await api.get(`/dashboard/stats${unitParam}`);
      if (myReqId !== reqIdRef.current) return;
      setStats(statsRes.data);
      // PM Due — derive counts from the existing predicted-maintenance rows.
      // Due  = PM limit reached/passed (strokes_to_next_pm <= 0).
      // Upcoming = PM within the next 7 days (same "warning" window the Analytics
      //            page uses), not yet due. Total = Due + Upcoming. Non-blocking.
      try {
        const pmRes = await api.get(
          `/analytics/predicted-maintenance?unit_id=${selectedUnit.id}`, { silent403: true });
        if (myReqId !== reqIdRef.current) return;
        const rows = Array.isArray(pmRes.data) ? pmRes.data : [];
        let due = 0, upcoming = 0;
        for (const d of rows) {
          if (!d.pm_stroke_limit) continue;                       // no PM configured → skip
          if (d.strokes_to_next_pm != null && d.strokes_to_next_pm <= 0) due++;
          else if (d.days_to_next_pm != null && d.days_to_next_pm > 0 && d.days_to_next_pm <= 7) upcoming++;
        }
        setPmDue({ upcoming, due, total: upcoming + due });
      } catch {
        if (myReqId === reqIdRef.current) setPmDue(null);
      }
      /* RM Coverage — read STRAIGHT off the Raw Material stock statement, the
         same engine the Raw Material screen itself renders, so the bubble can
         never disagree with the page it summarises. No RM maths lives here.
         silent403: a user without Raw Material view simply does not get the
         bubble, exactly like the other permission-gated tiles. */
      // NOT awaited on purpose. Measured: the stock statement takes ~500-650ms
      // on the live units, and awaiting it here held the dashboard's own
      // loading spinner open for that whole time for a single tile. Fired
      // alongside instead — every other card paints immediately and the RM
      // ring fills in when it lands.
      // No year/month: the endpoint defaults to the SERVER's current month, the
      // same clock every other tile on this dashboard is built from. Sending
      // the browser's month here would have put the RM ring on a different
      // month to the BOP ring for the first 5.5 hours of IST on the 1st.
      {
        api.get(`/raw-material/stock-statement?unit_id=${selectedUnit.id}`,
                { silent403: true })
          .then((r) => {
            if (myReqId === reqIdRef.current) setRmCoverage(r.data?.summary || null);
          })
          .catch(() => {
            if (myReqId === reqIdRef.current) setRmCoverage(null);
          });
      }
    } catch (error) {
      if (myReqId !== reqIdRef.current) return;
      console.error("Error fetching dashboard data:", error);
    } finally {
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  };

  if (!selectedUnit) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <p className="text-muted-foreground mb-4">Please select a unit first</p>
        <Button onClick={() => navigate("/select-unit")}>Select Unit</Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 aampl-dash p-4 rounded-xl" data-testid="dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">{selectedUnit.name} Overview</p>
        </div>
        <div className="flex items-center gap-3">
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-white/80 dark:bg-slate-800/80 border border-border/50 shadow-sm px-3 py-1.5 text-sm font-medium text-foreground backdrop-blur"
            data-testid="yesterday-operations-pill"
            title="Operations entered yesterday"
          >
            <Spark color="#3b82f6" className="!w-10 !h-4" />
            {stats?.yesterday?.operations_count ?? 0} ops yesterday
          </span>
          {canArrange && (
            <ResetLayoutButton keys={[`dash-order-${selectedUnit?.id || "unit"}`,
                                      `dash-gauges-${selectedUnit?.id || "unit"}`]} />
          )}
          <Button onClick={() => navigate("/production-sheet")} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="production-sheet-btn">
            <Activity className="w-4 h-4 mr-2" />
            Production Sheet
          </Button>
        </div>
      </div>

      {/* Overview Stats — draggable (ADMIN ONLY): grab any card to rearrange; order is remembered per unit */}
      <DraggableGrid storageKey={`dash-order-${selectedUnit?.id || "unit"}`}
                     enabled={canArrange}
                     className="dash-stat-grid">
        <Card className="stat-card dash-tint-blue" data-testid="stat-maintenance-breakdown">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Wrench className="w-6 h-6 text-blue-500 dash-icon-wrench" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-muted-foreground">Yesterday's Maintenance &amp; Breakdown</p>
              <div className="flex items-center gap-3 mt-1">
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Maint.</p>
                  <p className="text-xl font-bold">{((stats?.yesterday?.preventive_count ?? 0) + (stats?.yesterday?.improvement_count ?? 0))}</p>
                </div>
                <div className="w-px h-8 bg-border"></div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-red-500" />B/D</p>
                  <p className="text-xl font-bold">{stats?.yesterday?.breakdown_count ?? 0}</p>
                </div>
                <div className="w-px h-8 bg-border"></div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Open</p>
                  <p className="text-xl font-bold text-red-500"
                     title="Breakdowns not yet closed — all dates, not just this day">
                    {stats?.yesterday?.open_breakdown_count ?? 0}
                  </p>
                </div>
              </div>
              {/* ROUND CJ — Maint./B-D are for the day named here (the latest day
                  that actually had maintenance activity, same convention as the
                  Inspection and FG Dispatch cards); Open is the running total. */}
              <p className="text-[10px] text-muted-foreground mt-0.5">
                Prev {stats?.yesterday?.preventive_count ?? 0} + Imp {stats?.yesterday?.improvement_count ?? 0}
                {stats?.yesterday?.maintenance_date ? ` · ${stats.yesterday.maintenance_date}` : ""}
              </p>
              {(stats?.yesterday?.open_breakdown_bad_date_count ?? 0) > 0 && (
                <button type="button"
                   onClick={() => navigate("/maintenance?issue=bad-date")}
                   className="block text-left text-[10px] text-amber-600 dark:text-amber-400 mt-0.5
                              underline decoration-dotted underline-offset-2 hover:text-amber-700 dark:hover:text-amber-300"
                   data-testid="dash-bd-bad-date"
                   title="Click to open these breakdowns on the Maintenance screen and fix their Opened-at date — they were saved with an impossible year, so they sit at the very bottom of the list and can never be found to close.">
                  ⚠ {stats.yesterday.open_breakdown_bad_date_count} of these have a wrong date — fix on Maintenance ›
                </button>
              )}
            </div>
          </div>
          {pmDue && (
            <div className="mt-3 pt-2 border-t border-border/60" data-testid="dash-pm-due">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">PM Due</p>
              <div className="flex items-center gap-3">
                <div>
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wide">Upcoming</p>
                  <p className="text-base font-bold leading-none text-yellow-600">{pmDue.upcoming}</p>
                </div>
                <div className="w-px h-6 bg-border"></div>
                <div>
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wide">Due</p>
                  <p className="text-base font-bold leading-none text-red-500">{pmDue.due}</p>
                </div>
                <div className="w-px h-6 bg-border"></div>
                <div>
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wide">Total</p>
                  <p className="text-base font-bold leading-none">{pmDue.total}</p>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* COMBINED (owner request 2026-09-21): Final Inspection actual +
            Inspection Efficiency in ONE card — same fields, same thresholds,
            zero recalculation. */}
        <Card className="stat-card dash-tint-green" data-testid="stat-final-inspection">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-teal-500/10 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-teal-500 dash-icon-check" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-muted-foreground">Yesterday's Inspection</p>
              <div className="flex items-center gap-3 mt-1">
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Inspected</p>
                  <p className="text-xl font-bold">{(stats?.yesterday?.final_inspection_total ?? 0).toLocaleString('en-IN')}</p>
                </div>
                <div className="w-px h-8 bg-border"></div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Efficiency</p>
                  <p className={`text-xl font-bold leading-none ${
                    (stats?.yesterday?.final_inspection_efficiency ?? 0) >= 90 ? 'text-green-500' :
                    (stats?.yesterday?.final_inspection_efficiency ?? 0) >= 70 ? 'text-orange-500' : 'text-red-500'
                  }`}>
                    {(stats?.yesterday?.final_inspection_efficiency ?? 0)}%
                    <LevelPill pct={stats?.yesterday?.final_inspection_efficiency} hi={90} lo={70} words={["On target", "Fair", "Low"]} />
                  </p>
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                incl. rejected{stats?.yesterday?.final_inspection_date ? ` · ${stats.yesterday.final_inspection_date}` : ""} · {(stats?.yesterday?.final_inspection_total ?? 0).toLocaleString('en-IN')} / {(stats?.yesterday?.final_inspection_plan ?? 0).toLocaleString('en-IN')} plan
              </p>
            </div>
          </div>
          <div className="mt-3 pt-2 border-t border-border/60">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">FG Addition</p>
                <p className="text-base font-bold leading-none text-green-600">{(stats?.yesterday?.fg_addition ?? 0).toLocaleString('en-IN')}</p>
                <p className="text-[9px] text-muted-foreground mt-0.5">good pcs added to FG stock</p>
              </div>
              <Spark color="#10b981" />
            </div>
          </div>
        </Card>

        <Card className="stat-card relative dash-tint-lavender" data-testid="stat-fg-dispatch">
          {/* Orange calendar trigger (top-left) — same lookup as the Production card */}
          <button
            type="button"
            onClick={() => (fgDateRef.current?.showPicker ? fgDateRef.current.showPicker() : fgDateRef.current?.focus())}
            className="absolute top-2 left-2 w-7 h-7 rounded-md bg-orange-500/10 flex items-center justify-center hover:bg-orange-500/20 transition cursor-pointer z-10"
            title="Click to pick a date"
            data-testid="dash-fg-date-btn"
          >
            <Calendar className="w-4 h-4 text-orange-500" />
          </button>
          {fgDate && (
            <div className="absolute top-2 right-2 flex items-center gap-1 text-[10px] text-muted-foreground">
              <span>{fgDisp?.date || fgDate}</span>
              <button
                onClick={() => { setFgDate(""); setFgDisp(null); }}
                className="hover:text-foreground"
                title="Back to yesterday"
                data-testid="dash-fg-date-clear"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <input
            ref={fgDateRef}
            type="date"
            value={fgDate}
            max={todayISO}
            onChange={(e) => { setFgDate(e.target.value); fetchFgForDate(e.target.value); }}
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
            data-testid="dash-fg-date"
          />
          <div className="flex items-center gap-4 pt-4">
            <div className="w-12 h-12 rounded-lg bg-indigo-500/10 flex items-center justify-center">
              <Truck className="w-6 h-6 text-indigo-500 dash-icon-truck" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{fgDisp ? `FG Dispatch on ${fgDisp.date}` : "Yesterday's FG Dispatch"}</p>
              <p className="metric-value">{fgDispLoading ? "…" : (fgDisp ? fgDisp.pcs : (stats?.yesterday?.fg_dispatch_qty ?? 0)).toLocaleString('en-IN')}</p>
              <p className="text-[10px] text-muted-foreground">pcs dispatched{(fgDisp ? fgDisp.date : stats?.yesterday?.fg_dispatch_date) ? ` · ${fgDisp ? fgDisp.date : stats.yesterday.fg_dispatch_date}` : ""}</p>
              <div className="mt-2 rounded-md bg-muted/40 px-2 py-1">
                <p className="text-[10px] text-muted-foreground">Dispatch value</p>
                <p className="text-sm font-semibold text-indigo-600">₹{(fgDisp ? fgDisp.value : (stats?.yesterday?.fg_dispatch_value ?? 0)).toLocaleString('en-IN')}</p>
                <p className="text-[9px] text-muted-foreground">by part price — parts without a price are not counted</p>
              </div>
            </div>
          </div>
        </Card>

        {stats?.bop_supply && stats.bop_supply.pct !== null && stats.bop_supply.pct !== undefined && (
          <Card className="stat-card dash-accent-violet" data-testid="stat-bop-supply">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                <Package className="w-5 h-5 dash-icon-pop" />
              </div>
              <p className="text-sm font-medium leading-tight">BOP Supply Compliance</p>
            </div>
            <BigRing pct={stats.bop_supply.pct}
                     num={(stats.bop_supply.cum_received ?? 0).toLocaleString('en-IN')}
                     den={(stats.bop_supply.sched_to_date ?? 0).toLocaleString('en-IN')} />
            <p className="text-[10px] text-center leading-tight">
              {(stats.bop_supply.cum_received ?? 0).toLocaleString('en-IN')} received / {(stats.bop_supply.sched_to_date ?? 0).toLocaleString('en-IN')}
            </p>
            <p className="text-[9px] text-center leading-tight opacity-90">
              month plan {(stats.bop_supply.monthly_total ?? 0).toLocaleString('en-IN')} · pending {(stats.bop_supply.pending ?? 0).toLocaleString('en-IN')} · {stats.bop_supply.parts ?? 0} BOP part{(stats.bop_supply.parts ?? 0) === 1 ? '' : 's'}
            </p>
          </Card>
        )}

        {/* ===== RM Coverage =====
            The RM counterpart to BOP / Jobwork compliance, and deliberately the
            same question those two answer: of the parts this month's plan
            actually needs, how many are CLEAR to run?

            covered = scheduled parts − parts short of raw material. Both figures
            come from /raw-material/stock-statement's own summary (shortage_parts,
            scheduled_parts), which already counts a shared coil pool ONCE rather
            than once per member — the reason this is read from there instead of
            recomputed: a second count would quietly disagree with the RM screen.

            Hidden when nothing is scheduled, so an idle month shows no ring
            rather than a meaningless 0%. */}
        {rmCoverage && (rmCoverage.month_required_kg ?? 0) > 0 && (() => {
          const req = rmCoverage.month_required_kg ?? 0;
          const recd = rmCoverage.month_received_kg ?? 0;
          const pct = Math.round((recd / req) * 1000) / 10;
          const kg = (n) => (Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
          return (
            <Card className="stat-card dash-accent-amber" data-testid="stat-rm-supply">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                  <Layers className="w-5 h-5 dash-icon-pop" />
                </div>
                <p className="text-sm font-medium leading-tight">RM Supply Compliance</p>
              </div>
              <BigRing pct={pct} num={kg(recd)} den={kg(req)} />
              <p className="text-[10px] text-center leading-tight">
                {kg(recd)} kg received / {kg(req)} kg month plan
              </p>
              <p className="text-[9px] text-center leading-tight opacity-90">
                in stock {kg(rmCoverage.total_rm_kg)} kg · pending {kg(rmCoverage.total_to_procure_kg)} kg · {rmCoverage.rm_parts ?? 0} RM part{(rmCoverage.rm_parts ?? 0) === 1 ? '' : 's'}
              </p>
            </Card>
          );
        })()}

        {stats?.jobwork_supply && stats.jobwork_supply.pct !== null && stats.jobwork_supply.pct !== undefined && (
          <Card className="stat-card dash-accent-teal" data-testid="stat-jobwork-supply">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                <Package className="w-5 h-5 dash-icon-pop" />
              </div>
              <p className="text-sm font-medium leading-tight">Jobwork Compliance</p>
            </div>
            <BigRing pct={stats.jobwork_supply.pct}
                     num={(stats.jobwork_supply.received ?? 0).toLocaleString('en-IN')}
                     den={(stats.jobwork_supply.sched_to_date ?? 0).toLocaleString('en-IN')} />
            <p className="text-[9px] text-center leading-tight opacity-90">
              month plan {(stats.jobwork_supply.monthly_total ?? 0).toLocaleString('en-IN')} · sent {(stats.jobwork_supply.sent ?? 0).toLocaleString('en-IN')} · pending {(stats.jobwork_supply.pending ?? 0).toLocaleString('en-IN')} · {stats.jobwork_supply.parts ?? 0} jobwork parts
            </p>
          </Card>
        )}

        {stats?.dispatch_supply && (stats.dispatch_supply.monthly_total ?? 0) > 0 && (() => {
          // Simple month view: % of the WHOLE month plan already dispatched.
          // (No "expected by today" proration — same fields, plain division.)
          const _ds = stats.dispatch_supply;
          const _monthPct = Math.round((_ds.dispatched / _ds.monthly_total) * 1000) / 10;
          // Value-wise view: dispatched ₹ vs plan ₹ (both priced from the part
          // master), computed server-side as value_pct.
          const _valPct = _ds.value_pct;
          return (
          <Card className="stat-card dash-tint-blue" data-testid="stat-dispatch-supply">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-emerald-500 dash-icon-pop" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Schedule vs Supply</p>
                <p className="metric-value">{_monthPct}%
                  <span className="ml-1 text-xs font-medium text-muted-foreground">of month plan done (pcs)</span>
                </p>
                {_valPct != null && (
                  <p className="text-sm font-bold text-emerald-600" data-testid="dispatch-supply-value-pct">
                    ₹ {_valPct}%
                    <span className="ml-1 text-[10px] font-medium text-muted-foreground">
                      value-wise — ₹{(_ds.value ?? 0).toLocaleString('en-IN')} / ₹{(_ds.monthly_total_value ?? 0).toLocaleString('en-IN')} plan
                    </span>
                  </p>
                )}
                <p className="text-[10px] text-muted-foreground">
                  {(_ds.dispatched ?? 0).toLocaleString('en-IN')} dispatched / {(_ds.monthly_total ?? 0).toLocaleString('en-IN')} month plan
                </p>
                <Spark color="#1687D9" />
                <p className="text-[9px] text-muted-foreground">
                  remaining {(_ds.pending ?? 0).toLocaleString('en-IN')} pcs{_valPct != null ? ` · ₹${(_ds.pending_value ?? 0).toLocaleString('en-IN')} value` : ""} · {_ds.parts ?? 0} scheduled part{(_ds.parts ?? 0) === 1 ? '' : 's'}
                </p>
              </div>
              <MiniRing pct={_valPct != null ? _valPct : _monthPct} color="#10b981" />
            </div>
          </Card>
          );
        })()}

        {/* ROUND CI — Shortage + Maintenance prediction share ONE bubble. Both are
            "what is about to go wrong" signals and each filled only the top of its
            card, leaving a large empty block underneath; stacked they use that space
            and free a slot in the row. Each half keeps its own click target, so
            Shortage still opens Inventory and Maintenance still opens Machine Health.
            Numbers, colours and wording are unchanged — layout only. */}
        {(shortPred || maintPred) && (
          <Card className="stat-card dash-shortage-card !p-0 overflow-hidden"
                data-testid="stat-predictions">
            {shortPred && (
              <div
                className="dash-pred-half cursor-pointer"
                data-testid="stat-shortage-prediction"
                onClick={() => navigate("/inventory?shortage=1")}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 shrink-0 rounded-lg bg-violet-500/10 flex items-center justify-center">
                    <span className="text-2xl dash-crystal">🔮</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-muted-foreground">Shortage Prediction</p>
                    <p className="metric-value">
                      {shortPred.parts_at_risk ?? 0}
                      <span className="ml-1 text-xs font-medium text-muted-foreground">part{(shortPred.parts_at_risk ?? 0) === 1 ? "" : "s"} at risk</span>
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {(shortPred.counts?.CRITICAL || 0) > 0 && <span className="text-red-600 font-semibold mr-1.5">🔴 {shortPred.counts.CRITICAL} critical</span>}
                      {(shortPred.counts?.HIGH || 0) > 0 && <span className="text-orange-600 font-semibold mr-1.5">🟠 {shortPred.counts.HIGH} high</span>}
                      {(shortPred.counts?.WATCH || 0) > 0 && <span className="text-amber-600 font-semibold">🟡 {shortPred.counts.WATCH} watch</span>}
                      {!(shortPred.counts?.CRITICAL || shortPred.counts?.HIGH || shortPred.counts?.WATCH) && <span className="text-emerald-600 font-semibold">🟢 no shortage predicted</span>}
                    </p>
                    <p className="text-[9px] text-violet-600 dark:text-violet-400 font-medium mt-0.5">View predictions →</p>
                  </div>
                </div>
              </div>
            )}

            {shortPred && maintPred && <div className="dash-pred-rule" />}

            {maintPred && (
              <div
                className="dash-pred-half cursor-pointer"
                data-testid="stat-maintenance-prediction"
                onClick={openMachineHealth}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 shrink-0 rounded-lg bg-amber-500/10 flex items-center justify-center">
                    <span className="text-2xl dash-gear">🛠️</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-muted-foreground">Maintenance Prediction</p>
                    <p className="metric-value">
                      {maintPred.needs_attention ?? 0}
                      <span className="ml-1 text-xs font-medium text-muted-foreground">tool{(maintPred.needs_attention ?? 0) === 1 ? "" : "s"} need attention</span>
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {(maintPred.counts?.CRITICAL || 0) > 0 && <span className="text-red-600 font-semibold mr-1.5">🔴 {maintPred.counts.CRITICAL} critical</span>}
                      {(maintPred.counts?.HIGH || 0) > 0 && <span className="text-orange-600 font-semibold mr-1.5">🟠 {maintPred.counts.HIGH} high</span>}
                      {(maintPred.counts?.WATCH || 0) > 0 && <span className="text-amber-600 font-semibold">🟡 {maintPred.counts.WATCH} watch</span>}
                      {!(maintPred.counts?.CRITICAL || maintPred.counts?.HIGH || maintPred.counts?.WATCH) && <span className="text-emerald-600 font-semibold">🟢 all tools normal</span>}
                    </p>
                    <p className="text-[9px] text-amber-600 dark:text-amber-400 font-medium mt-0.5">View Machine Health →</p>
                  </div>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* "Inspection Efficiency" card merged into stat-final-inspection above. */}

        <Card className="stat-card relative dash-tint-peach" data-testid="stat-today">
          {/* Custom-date chip + clear (only when a past date is picked) */}
          {prodDate && (
            <div className="absolute top-2 right-2 flex items-center gap-1 text-[10px] text-muted-foreground">
              <span>{dateProd?.date || prodDate}</span>
              <button
                onClick={() => { setProdDate(""); setDateProd(null); }}
                className="hover:text-foreground"
                title="Back to yesterday"
                data-testid="dash-prod-date-clear"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          {/* Hidden native date input — opened by clicking the calendar icon */}
          <input
            ref={prodDateRef}
            type="date"
            value={prodDate}
            max={todayISO}
            onChange={(e) => { setProdDate(e.target.value); fetchProductionForDate(e.target.value); }}
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
            data-testid="dash-prod-date"
          />
          {/* COMBINED (owner request 2026-09-21): Production actual + Production
              Efficiency in ONE card — same fields, same date-pick behavior,
              same thresholds, zero recalculation. */}
          {(() => {
            const eff = dateProd ? (dateProd.efficiency ?? 0) : (stats?.yesterday?.efficiency_percent ?? 0);
            const strokes = dateProd ? (dateProd.strokes ?? 0) : (stats?.yesterday?.total_production_strokes ?? 0);
            const target = dateProd ? (dateProd.target_strokes ?? 0) : (stats?.yesterday?.total_target_strokes ?? 0);
            return (
          <>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => (prodDateRef.current?.showPicker ? prodDateRef.current.showPicker() : prodDateRef.current?.focus())}
              className="w-12 h-12 rounded-lg bg-orange-500/10 flex items-center justify-center hover:bg-orange-500/20 transition cursor-pointer"
              title="Click to pick a date"
              data-testid="dash-prod-date-btn"
            >
              <Calendar className="w-6 h-6 text-orange-500 dash-icon-pop" />
            </button>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-muted-foreground">
                {dateProd ? `Production on ${dateProd.date}` : "Yesterday's Production"}
              </p>
              <div className="flex items-center gap-3 mt-1">
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Strokes</p>
                  <p className="text-xl font-bold">{dateProdLoading ? "…" : strokes.toLocaleString('en-IN')}</p>
                </div>
                <div className="w-px h-8 bg-border"></div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Efficiency</p>
                  <p className={`text-xl font-bold leading-none ${
                    eff >= 90 ? 'text-green-500' :
                    eff >= 70 ? 'text-orange-500' : 'text-red-500'
                  }`}>
                    {dateProdLoading ? "…" : <>{eff}%<LevelPill pct={eff} hi={90} lo={70} words={["On target", "Fair", "Low"]} /></>}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-3 pt-2 border-t border-border/60">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">vs Target</p>
                <p className="text-sm font-semibold leading-none">
                  {strokes.toLocaleString('en-IN')} / {target.toLocaleString('en-IN')} <span className="text-[10px] font-normal text-muted-foreground">strokes</span>
                </p>
                {dateProd && (
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {dateProd.production.toLocaleString('en-IN')} pcs · {dateProd.efficiency}% eff
                  </p>
                )}
              </div>
              <MiniGauge pct={eff} />
            </div>
          </div>
          </>
            );
          })()}
        </Card>
        {/* "Yesterday's Production Efficiency" card merged into stat-today above. */}
      </DraggableGrid>

      {/* Monthly progress — semi-circular gauges (side by side on wide screens); drag is ADMIN ONLY */}
      <DraggableGrid storageKey={`dash-gauges-${selectedUnit?.id || "unit"}`}
                     enabled={canArrange}
                     className="dash-gauge-grid">
        <ProgressGauge
          testId="gauge-press-month"
          hue="orange"
          tag="Press + Welding"
          title={`${stats?.current_month?.month_name || ""} ${stats?.current_month?.year || ""} Progress`}
          subtitle="Press + Welding · monthly completion"
          plan={stats?.current_month?.total_plan}
          cumulative={stats?.current_month?.total_cumulative}
          pending={stats?.current_month?.total_pending}
          backlog={stats?.current_month?.total_backlog}
        />
        {stats?.machine_month?.visible && (
          <ProgressGauge
            testId="gauge-machine-month"
            hue="violet"
            title="Machine Shop Progress"
            subtitle="From machine entries × cavities"
            plan={stats.machine_month.total_plan}
            cumulative={stats.machine_month.total_cumulative}
            pending={stats.machine_month.total_pending}
            backlog={stats.machine_month.total_backlog}
          />
        )}
        {stats?.assembly_month?.visible && (
          <ProgressGauge
            testId="gauge-assembly-month"
            hue="violet"
            title="Assembly Progress"
            subtitle="Assembly join-op production"
            plan={stats.assembly_month.total_plan}
            cumulative={stats.assembly_month.total_cumulative}
            pending={stats.assembly_month.total_pending}
            backlog={stats.assembly_month.total_backlog}
          />
        )}
        {stats?.inspection_month?.visible && (
          <ProgressGauge
            testId="gauge-fi-month"
            hue="cyan"
            title="Final Inspection Progress"
            subtitle="From the FI team's own sheet"
            plan={stats.inspection_month.total_plan}
            cumulative={stats.inspection_month.total_cumulative}
            pending={stats.inspection_month.total_pending}
            backlog={stats.inspection_month.total_backlog}
          />
        )}
      </DraggableGrid>

      <div style={{display:"none"}} data-legacy-removed="quick-actions-and-recent-entries">
        {/* Removed Feb 11, 2026: Quick Actions + Recent Entries per Anmol. */}
      </div>

      {/* Alerts */}
      {stats?.current_month?.total_backlog > 0 && (
        <Card className="border-orange-500 bg-orange-50 dark:bg-orange-950/20">
          <CardContent className="py-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-500" />
            <div>
              <p className="font-medium text-orange-700 dark:text-orange-400">Production Backlog Alert</p>
              <p className="text-sm text-orange-600 dark:text-orange-500">
                You are {stats?.current_month?.total_backlog?.toLocaleString('en-IN')} units behind schedule this month.
                Consider increasing daily production targets.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      {/* 🛠️ Machine Health dialog — read-only advisory table over APPROVED history */}
      {maintOpen && (
        <Dialog open onOpenChange={(o) => { if (!o) { setMaintOpen(false); setMaintWhy(null); } }}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="text-base">🛠️ Machine / Tool Health — advisory</DialogTitle>
            </DialogHeader>
            {!maintRows ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Analyzing approved maintenance history…</p>
            ) : (
              <div className="max-h-[60vh] overflow-y-auto">
                <p className="text-[11px] text-muted-foreground mb-2">
                  Recent {maintRows.recent_days}d vs the tool's own {maintRows.baseline_days}d baseline · approved records only
                  {maintRows.pending_unapproved_breakdowns > 0 && <> · {maintRows.pending_unapproved_breakdowns} pending log(s) excluded until approved</>}
                  . Advisory only — nothing is changed or scheduled automatically.
                </p>
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr className="text-left">
                      <th className="p-1.5">Risk</th><th className="p-1.5">Tool / Operation</th>
                      <th className="p-1.5">Part</th>
                      <th className="p-1.5 text-right">MTBF</th><th className="p-1.5 text-right">MTTR</th>
                      <th className="p-1.5 text-right">30d brk</th><th className="p-1.5 text-right">30d down</th>
                      <th className="p-1.5">Last</th><th className="p-1.5">Trend</th><th className="p-1.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(maintRows.rows || []).filter((r) => r.risk_level !== "NORMAL").slice(0, 60).map((r, i) => {
                      const cls = r.risk_level === "CRITICAL" ? "bg-red-100 text-red-700"
                        : r.risk_level === "HIGH" ? "bg-orange-100 text-orange-700"
                        : r.risk_level === "WATCH" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600";
                      const em = r.risk_level === "CRITICAL" ? "🔴" : r.risk_level === "HIGH" ? "🟠"
                        : r.risk_level === "WATCH" ? "🟡" : "⚪";
                      const ar = (t) => t === "up" ? "↑" : t === "down" ? "↓" : "→";
                      return (
                        <React.Fragment key={r.machine_id || i}>
                          <tr className="border-b hover:bg-accent/40">
                            <td className="p-1.5"><span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${cls}`}>{em} {r.risk_level}</span></td>
                            <td className="p-1.5 font-medium">{r.machine_name}</td>
                            <td className="p-1.5 text-muted-foreground">{r.part_no || "—"}</td>
                            <td className="p-1.5 text-right tabular-nums">{r.mtbf_hours != null ? `${Number(r.mtbf_hours).toLocaleString("en-IN")} h` : "—"}</td>
                            <td className="p-1.5 text-right tabular-nums">{r.mttr_hours != null ? `${r.mttr_hours} h` : "—"}</td>
                            <td className="p-1.5 text-right tabular-nums">{r.breakdown_count_recent}</td>
                            <td className="p-1.5 text-right tabular-nums">{r.recent_downtime_hours} h</td>
                            <td className="p-1.5 text-muted-foreground">{r.last_breakdown || "—"}</td>
                            <td className="p-1.5">{r.trend ? <>freq {ar(r.trend.frequency)} · mttr {ar(r.trend.mttr)}</> : "—"}</td>
                            <td className="p-1.5">
                              <button type="button" className="text-[10px] underline underline-offset-2"
                                      onClick={() => setMaintWhy(maintWhy === (r.machine_id || i) ? null : (r.machine_id || i))}>Why?</button>
                            </td>
                          </tr>
                          {maintWhy === (r.machine_id || i) && (
                            <tr className="border-b bg-amber-50/60 dark:bg-amber-950/20">
                              <td colSpan={10} className="p-2 text-[11px] leading-5">
                                {r.reason} <span className="text-muted-foreground">({r.data_quality}; confidence {r.confidence})</span>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {(maintRows.rows || []).filter((r) => r.risk_level !== "NORMAL").length === 0 && (
                      <tr><td colSpan={10} className="p-4 text-center text-muted-foreground">🟢 All tools are behaving within their own historical baseline.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

    </div>
  );
}
